import {
  AUTO_FILL_UPGRADE_AMOUNT,
  AUTO_FILL_UPGRADE_PACKAGE_ID,
  CURRENT_ACTIVATION_FEE,
  isCurrentTierActivationFee,
  LEGACY_ACTIVATION_FEE,
  PREVIOUS_CURRENT_ACTIVATION_FEE,
} from "../config/membershipConfig";

/** Hide ₹150 Auto Fill SKUs from the public membership catalog (upgrade is in-app only). */
export function isPublicMembershipPackageListing(pkg) {
  if (!pkg) return false;
  if (pkg.isAutoFillUpgrade === true) return false;
  if (pkg.category === "Auto Fill Upgrade") return false;
  if (pkg.id === AUTO_FILL_UPGRADE_PACKAGE_ID) return false;

  const price = Number(pkg.price);
  const name = (pkg.name || "").trim().toLowerCase();
  if (
    !pkg.hasSubItems &&
    price === AUTO_FILL_UPGRADE_AMOUNT &&
    price !== CURRENT_ACTIVATION_FEE
  ) {
    return false;
  }
  if (name === "150" || name === "auto filling program") {
    return false;
  }

  return true;
}

/**
 * Activation fee stored on confirmed package / order (admin confirm).
 * Avoid defaulting to ₹649 when the SKU is legacy ₹499 Activation.
 */
export function resolveActivationFeeFromPackageOrder(packageData, order) {
  const candidates = [
    packageData?.activationFee,
    packageData?.subItemsData?.activationFee,
    order?.subItemsData?.activationFee,
  ];
  for (const value of candidates) {
    if (value != null && value !== "") {
      const n = Number(value);
      if (!Number.isNaN(n)) return n;
    }
  }

  if (packageData?.isAutoFillUpgrade) return CURRENT_ACTIVATION_FEE;

  const price = Number(packageData?.price);
  const name = (packageData?.name || "").toLowerCase();
  if (price === LEGACY_ACTIVATION_FEE) return LEGACY_ACTIVATION_FEE;
  if (name.includes("activation") && price > 0 && price <= LEGACY_ACTIVATION_FEE) {
    return LEGACY_ACTIVATION_FEE;
  }
  if (price === CURRENT_ACTIVATION_FEE) return CURRENT_ACTIVATION_FEE;
  if (packageData?.isSubItemPackage) return CURRENT_ACTIVATION_FEE;

  return CURRENT_ACTIVATION_FEE;
}

export function resolvePurchasedActivationFee(userProfile) {
  if (!userProfile) return null;

  const pkgActivation =
    userProfile.purchasedPackage?.activationFee != null
      ? Number(userProfile.purchasedPackage.activationFee)
      : null;
  const topLevel =
    userProfile.purchasedActivationFee != null
      ? Number(userProfile.purchasedActivationFee)
      : null;

  // Embedded package record wins for explicit legacy tier (fixes bad top-level 649)
  if (pkgActivation === LEGACY_ACTIVATION_FEE) return LEGACY_ACTIVATION_FEE;
  if (pkgActivation === CURRENT_ACTIVATION_FEE) return CURRENT_ACTIVATION_FEE;
  if (pkgActivation === PREVIOUS_CURRENT_ACTIVATION_FEE) {
    return PREVIOUS_CURRENT_ACTIVATION_FEE;
  }

  if (topLevel != null && !Number.isNaN(topLevel)) return topLevel;

  if (userProfile.pendingPackagePurchase?.activationFee != null) {
    return Number(userProfile.pendingPackagePurchase.activationFee);
  }

  const purchased = userProfile.purchasedPackage;
  if (purchased?.price != null) {
    const price = Number(purchased.price);
    if (price === LEGACY_ACTIVATION_FEE) {
      return LEGACY_ACTIVATION_FEE;
    }
    if (price === CURRENT_ACTIVATION_FEE || price === PREVIOUS_CURRENT_ACTIVATION_FEE) {
      const name = (purchased.name || "").toLowerCase();
      if (name.includes("activation") || name.includes("ultra membership")) {
        return price;
      }
    }
  }

  return null;
}

/**
 * Legacy ₹499 activation tier (incl. standalone Activation package) who have
 * not yet purchased the Auto Filling Program (₹150) upgrade.
 */
export function isEligibleForAutoFillUpgrade(userProfile) {
  if (!userProfile?.hasPurchasedPackage) return false;
  if (userProfile.autoFillUpgradeCashbackCredited === true) return false;

  const fee = resolvePurchasedActivationFee(userProfile);
  if (Number.isNaN(fee)) return false;
  if (isCurrentTierActivationFee(fee)) return false;

  if (fee === LEGACY_ACTIVATION_FEE) return true;

  // Sub-items membership confirmed before we stored activationFee: still Ultra at old tier
  const pkg = userProfile.purchasedPackage;
  if (
    fee == null &&
    pkg &&
    Number(pkg.price) > LEGACY_ACTIVATION_FEE &&
    userProfile.purchasedActivationFee !== CURRENT_ACTIVATION_FEE
  ) {
    return true;
  }

  return false;
}

export function buildAutoFillUpgradePackage() {
  return {
    id: AUTO_FILL_UPGRADE_PACKAGE_ID,
    name: "Auto Filling Program",
    description:
      `Upgrade your membership activation from ₹${LEGACY_ACTIVATION_FEE} to ₹${CURRENT_ACTIVATION_FEE}. Pay the remaining ₹${AUTO_FILL_UPGRADE_AMOUNT} to unlock the Auto Filling Program (details coming soon).`,
    category: "Auto Fill Upgrade",
    price: AUTO_FILL_UPGRADE_AMOUNT,
    isAutoFillUpgrade: true,
    referralShare: false,
  };
}
